import { useState } from "react";
import { useAuth } from "react-oidc-context";
import styled from "styled-components";

import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "@ntemarked/mittne-components";
import Button from "@ntemarked/mittne-components/dist/Button/Button";

import { useStrombestillingApiMutation } from "hooks/useStrombestillingApi";
import { Section, SectionTitle, EmptyText, ErrorText, ButtonRow } from "pages/Sikring/components/SikringComponents";
import { QualityChecklistModal } from "pages/Sikring/oversikt/kvalitetssikring/QualityChecklistModal";
import { fmtDateTime, STATUS_LABEL } from "pages/Sikring/sikringUtils";
import { Kvalitetssikring, Sikring, Sikringsordrestatus } from "pages/Sikring/types";

interface QualityCheckSectionProps {
	orderId: string;
	status: Sikringsordrestatus;
	opprettetAv: string | null;
	kvalitetssikring: Kvalitetssikring | null;
	canEdit: boolean;
	sikring: Sikring;
	volumeKwh: number | null;
}

function qualityCheckErrorMessage(status: number): string {
	switch (status) {
		case 403:
			return "Sikringsordren kan ikke kvalitetssikres av den som opprettet den.";
		case 404:
			return "Fant ikke sikringsordren. Den kan være slettet.";
		default:
			return "Klarte ikke å oppdatere kvalitetssikringen. Prøv igjen senere.";
	}
}

/**
 * Hvorfor sikringsordren ikke kan kvalitetssikres av innlogget bruker, eller null når den kan det. Bare fullførte
 * sikringsordrer gir mening å kvalitetssikre, og backend avviser den som opprettet ordren.
 */
function qualityCheckBlocker(status: Sikringsordrestatus, createdByCurrentUser: boolean, canEdit: boolean): string | null {
	if (status !== Sikringsordrestatus.Completed) return `Bare fullførte sikringsordrer kan kvalitetssikres. Denne har status «${STATUS_LABEL[status]}».`;
	if (createdByCurrentUser) return "Du registrerte sikringsordren, så den må kvalitetssikres av noen andre.";
	if (!canEdit) return "Du har ikke tilgang til å kvalitetssikre sikringsordrer.";
	return null;
}

function useCurrentUserName(): string | null {
	const profile = useAuth().user?.profile;
	const name = [profile?.given_name, profile?.family_name].filter(Boolean).join(" ");
	return name || null;
}

export function QualityCheckSection({ orderId, status, opprettetAv, kvalitetssikring, canEdit, sikring, volumeKwh }: Readonly<QualityCheckSectionProps>) {
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [checklistOpen, setChecklistOpen] = useState(false);
	const mutation = useStrombestillingApiMutation(`/sikringsordre/${orderId}/kvalitetssikret`, ["sikringsordre"], "PUT", true);
	// Backend avviser kvalitetssikring fra den som opprettet ordren. Sjekkes her også, så knappen ikke tilbys forgjeves.
	const currentUserName = useCurrentUserName();
	const createdByCurrentUser = !!opprettetAv && opprettetAv === currentUserName;
	const blocker = qualityCheckBlocker(status, createdByCurrentUser, canEdit);

	async function setQualityChecked(kvalitetssikret: boolean) {
		setErrorMessage(null);
		try {
			const response = await mutation.mutateAsync({ kvalitetssikret });
			if (!response.ok) {
				setErrorMessage(qualityCheckErrorMessage(response.status));
				return;
			}
			setChecklistOpen(false);
		} catch {
			setErrorMessage(qualityCheckErrorMessage(0));
		}
	}

	function closeChecklist() {
		if (mutation.isPending) return;
		setChecklistOpen(false);
		setErrorMessage(null);
	}

	return (
		<Section>
			<SectionTitle>Kvalitetssikring</SectionTitle>
			{kvalitetssikring ? (
				<QualityCheckedText>
					<FontAwesomeIcon icon={faCircleCheck} color={colors.green} />
					Kvalitetssikret av {kvalitetssikring.navn}, {fmtDateTime(kvalitetssikring.tidspunkt)}
				</QualityCheckedText>
			) : (
				<EmptyText>{blocker ?? "Sikringsordren er ikke kvalitetssikret ennå."}</EmptyText>
			)}
			{errorMessage && !checklistOpen && <ErrorText>{errorMessage}</ErrorText>}
			{!blocker && (
				<ButtonRow>
					{kvalitetssikring ? (
						<Button
							type="button"
							secondary
							label="Fjern kvalitetssikring"
							loading={mutation.isPending}
							disabled={mutation.isPending}
							onClick={() => setQualityChecked(false)}
						/>
					) : (
						<Button type="button" label="Kvalitetssikre" onClick={() => setChecklistOpen(true)} />
					)}
				</ButtonRow>
			)}
			{checklistOpen && (
				<QualityChecklistModal
					sikring={sikring}
					volumeKwh={volumeKwh}
					pending={mutation.isPending}
					errorMessage={errorMessage}
					onConfirm={() => setQualityChecked(true)}
					onClose={closeChecklist}
				/>
			)}
		</Section>
	);
}

const QualityCheckedText = styled.p`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin: 0;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;
